import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, useTheme, Alert, Button, Paper, Typography } from '@mui/material';
import EntityCreationForm from './EntityCreationForm';
import ExistingEntities from './ExistingEntities';
import EntityDiscoveryManagement from './EntityDiscoveryManagement';
import TokenExaminer from './TokenExaminer/TokenExaminer';
import GlobalTokenPoolBrowser from './Admin/GlobalTokenPoolBrowser';
import CSVImportWizard from './EntityManagement/CSVImportWizard';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './ErrorFallback';
import useApiErrorHandler from '../hooks/useApiErrorHandler';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../contexts/AuthContext';

// eslint-disable-next-line max-lines-per-function
function EntityManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [_entities, setEntities] = useState([]);
  const theme = useTheme();

  // Get isPlatformAdmin from JWT token claims via useAuth hook
  const { isPlatformAdmin } = useAuth();

  const {
    loading,
    error,
    executeApiCall,
    clearError
  } = useApiErrorHandler();

  // Fetch entities on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use our error handling hook to execute the API call
        const entitiesData = await executeApiCall(
          () => api.getEntities(),
          {
            operationName: 'Fetch Entities'
          }
        );

        // Handle both old format (array) and new format (object with entities array)
        if (Array.isArray(entitiesData)) {
          setEntities(entitiesData);
        } else if (entitiesData && entitiesData.entities) {
          setEntities(entitiesData.entities);
        } else {
          setEntities([]);
        }
      } catch (err) {
        console.error('Unhandled error fetching data:', err);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle refresh when using mock data
  const handleRefreshData = () => {
    clearError();
    const refreshEntities = async () => {
      try {
        const entitiesData = await executeApiCall(
          () => api.getEntities(),
          {
            operationName: 'Fetch Entities'
          }
        );

        // Handle both old format (array) and new format (object with entities array)
        if (Array.isArray(entitiesData)) {
          setEntities(entitiesData);
        } else if (entitiesData && entitiesData.entities) {
          setEntities(entitiesData.entities);
        } else {
          setEntities([]);
        }
      } catch (err) {
        console.error('Unhandled error refreshing entities:', err);
      }
    };

    refreshEntities();
  };

  // Handle entity creation success - switch to existing entities tab
  // For single entity creation, switch tabs. For bulk import, stay on results.
  const handleEntityCreated = (newEntity, _options = {}) => {
    // Refresh entities in background without triggering loading state
    // This prevents the CSVImportWizard from being unmounted during refresh
    const refreshEntities = async () => {
      try {
        // Call API directly without executeApiCall to avoid setting loading state
        // which would unmount child components
        const entitiesData = await api.getEntities();

        // Handle both old format (array) and new format (object with entities array)
        if (Array.isArray(entitiesData)) {
          setEntities(entitiesData);
        } else if (entitiesData && entitiesData.entities) {
          setEntities(entitiesData.entities);
        } else {
          setEntities([]);
        }
      } catch (err) {
        console.error('Error refreshing entities after creation:', err);
        // Don't set error state here - let the user continue viewing results
      }
    };

    refreshEntities();

    // Only switch tabs for single entity creation, not bulk import
    // Bulk import results have 'successful' property; single entity has 'id' or 'entityId'
    const isBulkImport = newEntity && typeof newEntity.successful === 'number';
    if (!isBulkImport) {
      setTabValue(2); // Switch to existing entities tab (now index 2)
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Paper elevation={0} sx={{ width: '100%', p: 3 }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRefreshData}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          }
        >
          Error loading data: {error.message}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Entity Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create and manage all types of entities including clients, organizations, people, projects, programs, events, and resources.
        </Typography>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="entity management tabs"
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            color: theme.palette.text.primary,
          },
          '& .Mui-selected': {
            color: theme.palette.primary.main,
            bgcolor: theme.palette.mode === 'light' ? 'grey.100' : 'grey.800',
          },
        }}
      >
        <Tab label="Relationship Intelligence" />
        <Tab label="Entity Discovery" />
        <Tab label="Existing Entities" />
        <Tab label="New Entity" />
        <Tab label="Bulk Import" />
        {isPlatformAdmin && <Tab label="Global Token Pool" />}
      </Tabs>

      <ErrorBoundary
        FallbackComponent={props =>
          <ErrorFallback
            {...props}
            additionalActions={
              <Button
                variant="outlined"
                onClick={handleRefreshData}
              >
                Reload Data
              </Button>
            }
          />
        }
      >
        {tabValue === 0 && <TokenExaminer />}
        {tabValue === 1 && <EntityDiscoveryManagement />}
        {tabValue === 2 && <ExistingEntities />}
        {tabValue === 3 && <EntityCreationForm onEntityCreated={handleEntityCreated} />}
        {tabValue === 4 && <CSVImportWizard onImportComplete={handleEntityCreated} />}
        {tabValue === 5 && isPlatformAdmin && <GlobalTokenPoolBrowser />}
      </ErrorBoundary>
    </Paper>
  );
}

export default EntityManagement;